/**
 * CC 控制操作栏
 *
 * Props:
 *   ccTaskInfo     { cost_usd, duration_ms, num_turns }  任务结束后的汇总
 *   ccTokenUsage   { input, output, cacheRead, turns }   实时累积 token 用量
 *   ccSessionState idle / running / requires_action
 *   onSendControl  (payload) => void
 */

/** 格式化 token 数，超过 1k 用 k 表示 */
const fmtTok = (n) => {
  if (n == null || n === 0) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const CCControlBar = ({ ccTaskInfo, ccTokenUsage, ccSessionState, onSendControl, onCompact, onClearHistory, onExportChatlog }) => {
  const handleInterrupt = () => {
    onSendControl({
      type: "control_request",
      request_id: `interrupt-${Date.now()}`,
      request: { subtype: "interrupt" },
    });
  };

  const isRunning = ccSessionState === "running";
  const hasTokens = ccTokenUsage && (ccTokenUsage.input > 0 || ccTokenUsage.output > 0);

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "4px 12px",
      borderTop: "1px solid #21262d",
      background: "#0d1117",
      fontSize: "11px",
      flexShrink: 0,
      minWidth: 0,
      overflow: "hidden",
    }}>
      {/* 中断按钮 */}
      <button
        onClick={handleInterrupt}
        disabled={!isRunning}
        title="中断当前 YouLabber 任务"
        style={{
          flexShrink: 0,
          background: "none",
          border: `1px solid ${isRunning ? "#f85149" : "#30363d"}`,
          color: isRunning ? "#f85149" : "#6e7681",
          borderRadius: "4px",
          padding: "2px 8px",
          cursor: isRunning ? "pointer" : "not-allowed",
          fontSize: "11px",
        }}
      >
        ⏹ 中断
      </button>

      <button
        onClick={onCompact}
        disabled={isRunning}
        title="压缩当前 YouLabber 上下文"
        style={{
          flexShrink: 0,
          background: "none",
          border: `1px solid ${isRunning ? "#30363d" : "#3b82f6"}`,
          color: isRunning ? "#6e7681" : "#58a6ff",
          borderRadius: "4px",
          padding: "2px 8px",
          cursor: isRunning ? "not-allowed" : "pointer",
          fontSize: "11px",
        }}
      >
        压缩上下文
      </button>

      {onExportChatlog && (
        <button
          onClick={onExportChatlog}
          disabled={isRunning}
          title="导出本机自上次清空以来的完整对话"
          style={{
            flexShrink: 0,
            background: "none",
            border: `1px solid ${isRunning ? "#30363d" : "#3fb950"}`,
            color: isRunning ? "#6e7681" : "#3fb950",
            borderRadius: "4px",
            padding: "2px 8px",
            cursor: isRunning ? "not-allowed" : "pointer",
            fontSize: "11px",
          }}
        >
          导出对话
        </button>
      )}

      <button
        onClick={onClearHistory}
        disabled={isRunning}
        title="对话将归档到本机 chatlogs 目录，导出时可选包含"
        style={{
          flexShrink: 0,
          background: "none",
          border: `1px solid ${isRunning ? "#30363d" : "#8b949e"}`,
          color: isRunning ? "#6e7681" : "#8b949e",
          borderRadius: "4px",
          padding: "2px 8px",
          cursor: isRunning ? "not-allowed" : "pointer",
          fontSize: "11px",
        }}
      >
        清空历史
      </button>

      {/* Token 实时用量 */}
      {hasTokens && (
        <span
          title={`输入: ${ccTokenUsage.input.toLocaleString()} tokens\n输出: ${ccTokenUsage.output.toLocaleString()} tokens\n缓存命中: ${ccTokenUsage.cacheRead.toLocaleString()} tokens\n轮次: ${ccTokenUsage.turns}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            color: "#6e7681",
            fontSize: "11px",
            flexShrink: 0,
          }}
        >
          <span style={{ color: "#58a6ff" }}>↑{fmtTok(ccTokenUsage.input)}</span>
          <span style={{ color: "#3fb950" }}>↓{fmtTok(ccTokenUsage.output)}</span>
          {ccTokenUsage.cacheRead > 0 && (
            <span style={{ color: "#d29922" }}>⚡{fmtTok(ccTokenUsage.cacheRead)}</span>
          )}
          {ccTokenUsage.turns > 0 && (
            <span style={{ color: "#8b949e" }}>{ccTokenUsage.turns}轮</span>
          )}
        </span>
      )}

      {/* 任务完成后：耗时（右对齐，不显示费用） */}
      {ccTaskInfo?.duration_ms != null && (
        <span style={{ marginLeft: "auto", color: "#6e7681", fontSize: "11px", flexShrink: 0, whiteSpace: "nowrap" }}>
          {(ccTaskInfo.duration_ms / 1000).toFixed(1)}s
        </span>
      )}
    </div>
  );
};

export default CCControlBar;
